/* eslint-disable @reatom/unit-naming-rule */
import { action, reatomMap } from '@reatom/framework'

import { Brand, Map } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { PairAddress } from '@root/entities/pair'
import { pairRegistry } from '@root/entities/pair/atoms'
import { userPositionUpdateEvent } from '@root/entities/realtime'
import { WalletId } from '@root/entities/wallet'

import {
  reatomAggregatedPosition,
  type AggregatedPositionModel,
} from './reatom-aggregated-position'
import { reatomWalletPosition } from './reatom-wallet-position'

import type { tradeApi } from '@blade/socket-types'
import type { UserApi } from '@root/shared/api'

type PositionKey = Brand.Branded<string, 'PositionKey'>
// eslint-disable-next-line @typescript-eslint/no-redeclare
const PositionKey = Brand.nominal<PositionKey>()

export const positionRegistryKey = (
  address: PairAddress,
  network: Network.Network,
): PositionKey => {
  return PositionKey(`${network}:${address}`)
}

export class PositionsRegistry {
  public positionsAtom = reatomMap(
    Map.create<PositionKey, AggregatedPositionModel>(),
  )

  public get = action(
    (ctx, pairAddress: PairAddress, network: Network.Network) => {
      const key = positionRegistryKey(pairAddress, network)
      return this.positionsAtom.get(ctx, key)
    },
  )

  public resolveFromDto = action((ctx, dto: UserApi.AggregatedPositionDto) => {
    const existingPosition = this.get(
      ctx,
      PairAddress(dto.poolAddress),
      Network.fromDto(dto.network),
    )
    if (existingPosition) {
      existingPosition.updateFromDto(ctx, dto)
      return existingPosition
    }

    const pair = pairRegistry.resolveFromDto(ctx, dto.pair)
    const position = reatomAggregatedPosition({ dto, pair })
    this.positionsAtom.set(
      ctx,
      positionRegistryKey(pair.address, pair.network),
      position,
    )
    return position
  })

  public updateWalletPositionFromDto = action(
    (ctx, dto: UserApi.UserPositionDto | tradeApi.UserPositionData) => {
      const model = positionsRegistry.get(
        ctx,
        PairAddress(
          typeof dto.pair === 'string' ? dto.pair : dto.pair.poolAddress,
        ),
        Network.fromDto(dto.network),
      )

      if (!model) {
        return
      }

      const existingWalletPosition = model.positionsByWallet.get(
        ctx,
        WalletId(dto.walletId),
      )
      if (existingWalletPosition) {
        existingWalletPosition.updateFromDto(ctx, dto)
        return
      }

      const walletPosition = reatomWalletPosition({
        dto: dto,
        pair: model.pair,
      })
      model.positionsByWallet.set(ctx, walletPosition.walletId, walletPosition)
    },
  )
}

export const positionsRegistry = new PositionsRegistry()

export const positionsDtoUpdatedEvent = action<UserApi.AggregatedPositionDto>(
  'positionsDtoUpdatedEvent',
)

positionsDtoUpdatedEvent.onCall((ctx, _, [dto]) => {
  const position = positionsRegistry.resolveFromDto(ctx, dto)
  position.updateFromDto(ctx, dto)
})

userPositionUpdateEvent.onCall((ctx, update) => {
  positionsRegistry.updateWalletPositionFromDto(ctx, update)
})
